// project-context.js

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { apiFetch } from '../../api/api';
import { useOrg } from '../orgs/org-context';

const ProjectContext = createContext(null);

export function ProjectProvider({ children }) {

  const {
    activeOrgId
  } = useOrg();

  const [projects, setProjects] = useState([]);

  const [activeProjectId, setActiveProjectId] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  // ----------------------------------------
  // LOAD PROJECTS
  // ----------------------------------------

  async function fetchProjects() {

    if (!activeOrgId) {

      setProjects([]);
      setActiveProjectId(null);

      return;
    }

    try {

      setLoading(true);

      const res = await apiFetch(
        '/datasets/list',
        {
          method: 'POST',
          body: {
            cnt: 0,
            lmt: 1000
          }
        }
      );

      const items =
        res?.items ||
        res?.data ||
        res ||
        [];

      setProjects(items);

    } catch (err) {

      console.error(
        'Failed loading projects:',
        err
      );

    } finally {

      setLoading(false);

    }
  }

  // ----------------------------------------
  // SWITCH PROJECT
  // ----------------------------------------

  const switchProject = (projectId) => {

    const project =
      projects.find(
        p => String(p._id) === String(projectId)
      );

    if (!project) return;

    setActiveProjectId(projectId);

    localStorage.setItem(
      'activeProjectId',
      String(projectId)
    );
  };

  // ----------------------------------------
  // RESTORE SAVED PROJECT
  // ----------------------------------------

  useEffect(() => {

    if (!projects.length) return;

    const saved =
      localStorage.getItem(
        'activeProjectId'
      );

    if (!saved) return;

    const exists =
      projects.find(
        p => String(p._id) === String(saved)
      );

    if (exists) {
      setActiveProjectId(saved);
    }

  }, [projects]);

  // ----------------------------------------
  // RESET ON ORG CHANGE
  // ----------------------------------------

  const previousOrgRef = useRef(null);

  useEffect(() => {

    if (
      previousOrgRef.current &&
      previousOrgRef.current !== activeOrgId
    ) {

      setProjects([]);
      setActiveProjectId(null);

      localStorage.removeItem(
        'activeProjectId'
      );
    }

    previousOrgRef.current =
      activeOrgId;

  }, [activeOrgId]);

  // ----------------------------------------
  // BOOTSTRAP
  // ----------------------------------------

  const bootstrappedRef =
    useRef(false);

  useEffect(() => {

    if (!activeOrgId) {

      setProjects([]);
      setActiveProjectId(null);

      bootstrappedRef.current = false;

      return;
    }

    if (bootstrappedRef.current) return;

    bootstrappedRef.current = true;

    fetchProjects();

  }, [activeOrgId]);

  // ----------------------------------------
  // AUTO SELECT SINGLE PROJECT
  // ----------------------------------------

  useEffect(() => {

    if (!projects.length) return;

    const saved =
      localStorage.getItem(
        'activeProjectId'
      );

    if (!saved && projects.length === 1) {

      const id = projects[0]._id;

      setActiveProjectId(id);

      localStorage.setItem(
        'activeProjectId',
        String(id)
      );
    }

  }, [projects]);

  // ----------------------------------------
  // ACTIVE PROJECT
  // ----------------------------------------

  const activeProject = useMemo(() => {

    return (
      projects.find(
        p =>
          String(p._id) ===
          String(activeProjectId)
      ) || null
    );

  }, [projects, activeProjectId]);

  // ----------------------------------------
  // PROVIDER
  // ----------------------------------------

  return (
    <ProjectContext.Provider
      value={{

        projects,

        activeProject,
        activeProjectId,

        switchProject,

        fetchProjects,

        loading

      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {

  const ctx =
    useContext(ProjectContext);

  if (!ctx) {

    throw new Error(
      'useProject must be used inside ProjectProvider'
    );
  }

  return ctx;
}
